import { getFocusTargets, $el, $list } from '../../utils/utils'
import { isEnterKey, isTabKey } from '../../utils/key'
import { Nullable } from '../../types/common'
import { BaseComponent } from '../base-component'
import { FloatElement } from './float-element'

export class DropdownOpener extends BaseComponent {
  hideTimeoutId: ReturnType<typeof setTimeout>
  element: HTMLElement

  constructor() {
    super()
    this.element = this.getElement()
  }

  mountComponent(): void {
    if (this.floatElement) {
      customElements.upgrade(this.floatElement) // https://developer.mozilla.org/en-US/docs/Web/API/CustomElementRegistry/upgrade
      this.floatElement?.on('show', this.handleDropdownShow)
      this.floatElement?.on('hide', this.handleDropdownHide)
    }

    this.addListener(this.element, 'blur', this.handleBlur)
    this.addListener(this.element, 'keydown', this.handleKeyDown)
    this.addListener(this.element, 'click', this.handleTrigger)
  }

  unmountComponent(): void {
    if (this.floatElement) {
      customElements.upgrade(this.floatElement) // https://developer.mozilla.org/en-US/docs/Web/API/CustomElementRegistry/upgrade
      this.floatElement?.off('show', this.handleDropdownShow)
      this.floatElement?.off('hide', this.handleDropdownHide)
    }

    clearTimeout(this.hideTimeoutId)
  }

  handleBlur = (): void => {
    this.hideTimeoutId = setTimeout(() => {
      this.hideFloatElement()
    }, 0)
  }

  handleDropdownShow = (): void => {
    this.setTargetListeners('add')
  }

  handleDropdownHide = (): void => {
    this.setTargetListeners('remove')
    clearTimeout(this.hideTimeoutId)
  }

  handleKeyDown = (event: KeyboardEvent): void => {
    const { firstTarget } = this.getTargets()

    if (isEnterKey(event)) {
      event.preventDefault()
      this.showFloatElement()
    }

    if (
      isTabKey(event) &&
      firstTarget &&
      this.floatElement.isOpen &&
      !event.shiftKey
    ) {
      event.preventDefault()
      firstTarget.focus()
      clearTimeout(this.hideTimeoutId)
    }
  }

  handleFirstTargetFocus = (): void => {}

  handleTargetFocus = (): void => {
    clearTimeout(this.hideTimeoutId)
  }

  handleLastTargetBlur = (): void => {
    this.hideTimeoutId = setTimeout(() => {
      this.hideFloatElement()
      this.focus()
    }, 0)
  }

  hideFloatElement() {
    if (this.floatElement) {
      this.floatElement.hide()
    }
  }

  showFloatElement() {
    if (this.floatElement) {
      this.floatElement.show()
    }
  }

  handleTrigger = (event: MouseEvent): void => {
    event.preventDefault()
    this.trigger()
  }

  handleUntrigger = ({ clientX: x, clientY: y, target }): void => {
    if (!this.floatElement) {
      return
    }

    const isCursorOutside = this.floatElement.checkCursorOutside(x, y)
    const isOutsideFloatElements = this.isTargetOutsideFloatElements(target)

    if (isCursorOutside && isOutsideFloatElements) {
      this.floatElement.hide()
    }
  }

  setTargetListeners(action: 'add' | 'remove'): void {
    const { targets, firstTarget, lastTarget } = this.getTargets()
    const listenerSetter = action === 'add' ? 'addListener' : 'removeListener'

    this[listenerSetter](firstTarget, 'focus', this.handleFirstTargetFocus)
    targets.forEach(target =>
      this[listenerSetter](target, 'focus', this.handleTargetFocus),
    )
    this[listenerSetter](lastTarget, 'blur', this.handleLastTargetBlur)
    this[listenerSetter](window, 'click', this.handleUntrigger)
  }

  getTargets(): any {
    if (!this.floatElement) {
      return
    }

    const element = this.floatElement.element
    const targets = $list(getFocusTargets(), element)
    const firstTarget = targets[0]
    const lastTarget = targets[targets.length - 1]

    return { targets, firstTarget, lastTarget }
  }

  isTargetOutsideFloatElements(target) {
    if (!this.floatElement) {
      return
    }

    const isInsideFloatElement = this.floatElement.contains(target)
    const isInsideFloatElementBtn = this.contains(target)

    return !isInsideFloatElement && !isInsideFloatElementBtn
  }

  getElement(): HTMLElement {
    return (
      $el(
        `[data-float-element-trigger="${this.dataset.floatElementId}"]`,
        this,
      ) || this
    )
  }

  trigger = (): void => {
    if (!this.floatElement) {
      return
    }

    this.floatElement.isOpen
      ? this.floatElement.hide()
      : this.floatElement.show()

    this.addListener(window, 'click', this.handleUntrigger)
  }

  setExpand(isExpanded): void {
    this.setAttribute('data-aria-expanded', isExpanded)
  }

  get floatElement(): Nullable<FloatElement> {
    return $el(`#${this.dataset.floatElementId}`)
  }
}
